// /w/:wid/titles — your titles: the one you wear, privacy, every earned title (tap to wear it, pick the tier), the
// ones still to earn, dimmed.
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { WorldPage, useWorldCity } from "@/app/WorldPage";
import { ScreenHero, Toggle } from "@/kit/city";
import { Button, Card, CardHead, Empty, Seg, Skeleton, cx } from "@/kit/primitives";
import { useSession } from "@/state/session";
import { useInit } from "@/state/queries";
import { usePrompts } from "@/state/prompts";
import { useTitleMutations, useTitles } from "@/state/socialQueries";
import { fmtNum, plural } from "@/game/format";
import { tierLine, titleBlurb, titleName, titleTiers, titlesOf, topTier } from "@/game/titles";
import { TitleBadge } from "./TitleBadge";
import { TitleSheet } from "./TitleSheet";
import "../social.css";

export function Titles() {
  return (
    <WorldPage layout="col">
      <TitlesBody />
    </WorldPage>
  );
}

function TitlesBody() {
  const { base } = useWorldCity();
  const nav = useNavigate();
  const session = useSession();
  const init = useInit();
  const q = useTitles(session.uid);
  const { setMain, setPrivacy } = useTitleMutations(session.uid);
  const t = titlesOf(q.data?.user?.titles ?? init.data?.user?.titles, init.data?.titles_config?.details);
  const [pick, setPick] = useState<string | null>(null);
  const [tier, setTier] = useState<number>(1);
  const pickEarned = !!pick && t.earned.includes(pick);

  const wear = async (title: string, tr: number) => {
    try {
      await setMain.mutateAsync({ title, tier: tr });
      setPick(null);
    } catch (e) {
      usePrompts.getState().error(e);
    }
  };
  const open = (title: string) => {
    const earnedTiers = t.tiers[title] ?? [];
    setTier(t.main?.title === title ? t.main.tier : earnedTiers.length ? earnedTiers[earnedTiers.length - 1] : 1);
    setPick(title);
  };

  return (
    <div className="stack">
      <ScreenHero
        title="Titles"
        blurb={t.main ? <>You wear <b className="gold">{titleName(t.main.title, t.details)}</b>{t.main.tier > 1 ? ` (tier ${t.main.tier})` : ""}. Other players see it on your cities and on the boards unless you keep titles private.</> : "Titles are earned by playing — conquering, trading, defending, leading a guild. Wear one and other players see it on your cities."}
        onBack={() => nav(`${base}/player/${session.uid}`)}
        stats={[
          { label: "Earned", value: fmtNum(t.earnedCount) },
          { label: "Still to earn", value: fmtNum(t.unearned.length) },
          { label: "Shown to others", value: t.privacy ? "No" : "Yes" },
        ]}
      >
        <div style={{ marginTop: 16, display: "flex", alignItems: "center", gap: 12 }}>
          <Toggle on={!t.privacy} label={t.privacy ? "Titles private — tap to show them" : "Titles public — tap to hide them"} busy={setPrivacy.isPending} onChange={(on) => setPrivacy.mutate(on ? 0 : 1, { onError: (e) => usePrompts.getState().error(e) })} />
          <span className="muted" style={{ font: "400 var(--fs-meta)/1.2 var(--text)" }}>{t.privacy ? "Hidden from other players" : "Shown to other players"}</span>
        </div>
      </ScreenHero>

      {q.isLoading && !q.data && <Skeleton h={300} />}
      {q.isError && !q.data && <Card><Empty>Couldn't read your titles. <Button size="sm" onClick={() => void q.refetch()}>Try again</Button></Empty></Card>}

      <Card>
        <CardHead title="Earned" aside={plural(t.earnedCount, "title")} />
        {!t.earned.length && <Empty>You have not been awarded any titles (yet).</Empty>}
        <div className="tt-grid">
          {t.earned.map((name) => {
            const isMain = t.main?.title === name;
            const tiers = titleTiers(name, t.details, t.tiers[name]);
            const have = t.tiers[name] ?? [];
            return (
              <button key={name} type="button" className={cx("tt-card", isMain && "on")} onClick={() => open(name)} aria-pressed={isMain}>
                {/* at its highest tier, as the live app shows a title (TitlePrefabScript) */}
                <TitleBadge title={name} tier={topTier(t, name)} details={t.details} size={52} glow={isMain} />
                <span className="txt">
                  <b>{titleName(name, t.details)}</b>
                  <span>{titleBlurb(name, t.details) || (have.length ? `tier ${Math.max(...have)}` : "earned")}</span>
                  {tiers.length > 1 && <span className="tt-tiers" aria-label={`tiers ${have.join(", ")} of ${tiers.length}`}>{tiers.map((n) => <i key={n} className={cx(have.includes(n) && "on")} />)}</span>}
                </span>
                {isMain && <span className="main">Worn</span>}
              </button>
            );
          })}
        </div>
      </Card>

      {t.unearned.length > 0 && (
        <Card>
          <CardHead title="Still to earn" aside={plural(t.unearned.length, "title")} />
          <div className="tt-grid">
            {t.unearned.map((name) => (
              <button key={name} type="button" className="tt-card locked" onClick={() => setPick(name)}>
                <TitleBadge title={name} tier={titleTiers(name, t.details)[0]} details={t.details} size={52} dim />
                <span className="txt"><b>{titleName(name, t.details)}</b><span>{titleBlurb(name, t.details) || "not yet"}</span></span>
              </button>
            ))}
          </div>
        </Card>
      )}

      <TitleSheet
        title={pick}
        tier={pickEarned ? tier : titleTiers(pick ?? "", t.details)[0]}
        details={t.details}
        onClose={() => setPick(null)}
        note={pick ? (pickEarned ? `${tierLine(t, pick, tier)}${t.main?.title === pick && t.main?.tier === tier ? " · worn" : ""}` : "Not earned yet") : undefined}
      >
        {pick && pickEarned && (
          <>
            {(t.tiers[pick]?.length ?? 0) > 1 && (
              <Seg value={String(tier)} onChange={(v) => setTier(Number(v))} options={(t.tiers[pick] ?? []).map((n) => ({ value: String(n), label: `Tier ${n}` }))} />
            )}
            <Button kind="gold" size="md" wide busy={setMain.isPending} disabled={t.main?.title === pick && t.main?.tier === tier} onClick={() => void wear(pick, tier)}>
              {t.main?.title === pick && t.main?.tier === tier ? "Already worn" : "Wear this title"}
            </Button>
          </>
        )}
      </TitleSheet>
    </div>
  );
}
